import { Component, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { Organization } from 'src/app/core/models/organization';
import { AuthService } from 'src/app/core/services/auth.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { GlobalModalService } from 'src/app/core/services/global-modal.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { LoadingIndicatorService } from 'src/app/core/services/loading-indicator.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { isEmpty } from 'src/app/core/utils/object-util';
import { SUPPORT_LINK_INFORMATION } from '../../constants/mapping-keys';
import { DELAY_TIME_MS } from '../../constants/typeahead';
import { MemberSearch } from '../../models/member-search';
import { MemberInformationService } from '../../services/member-information.service';
import { MessageService } from '../../services/message.service';
import { formatMemberQuickSearch } from '../../utils/autocomplete-util';
import { isString } from '../../utils/type-util';

enum SearchSource {
  USER_INPUT,
  INPUT_FOCUS
}

@Component({
  selector: 'th-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  private authenticatedHomePageRoute = '/private';
  private unauthenticatedHomePageRoute = '/';

  supportInformation: string;
  loggedOutSubscription: Subscription;
  searchValue: MemberSearch | string;
  showingRecentlyViewed = true;
  searching = false;
  searchingMemberList = false;
  searchResults: MemberSearch[] = [];
  formatMemberQuickSearch = formatMemberQuickSearch;
  memberId: string;
  memberIdSubscription: Subscription;

  searchInputFocus$ = new Subject<string>();

  constructor(
    public authService: AuthService,
    private modalService: NgbModal,
    private messageService: MessageService,
    public headerService: HeaderService,
    private router: Router,
    private globalModalService: GlobalModalService,
    private loadingIndicatorService: LoadingIndicatorService,
    private toastr: ToastrService,
    private organizationService: OrganizationService,
    public environmentService: EnvironmentService,
    private patientService: PatientService,
    public memberInformationService: MemberInformationService
  ) {}

  ngOnInit() {
    this.memberIdSubscription = this.memberInformationService.memberId.subscribe((memberId) => {
      this.memberId = memberId;
    });

    this.getSupportInformation();
    this.loggedOutSubscription = this.authService.loggedOut.subscribe(() => {
      this.searchValue = null;
      this.getSupportInformation();
    });
  }

  get homePageRoute(): string {
    if (this.authService.isAuthenticated()) {
      return this.authenticatedHomePageRoute;
    } else {
      return this.unauthenticatedHomePageRoute;
    }
  }

  private prepareQuickSearchSource(searchInputValue$: Observable<string>, source: SearchSource) {
    return searchInputValue$.pipe(
      tap(() => (this.searchingMemberList = false)),
      source === SearchSource.USER_INPUT
        ? debounceTime(DELAY_TIME_MS)
        : tap(() => {
            /* intentionally empty */
          }),
      source === SearchSource.USER_INPUT
        ? distinctUntilChanged()
        : tap(() => {
            /* intentionally empty */
          })
    );
  }

  searchMemberList = (text$: Observable<string>) =>
    merge(
      this.prepareQuickSearchSource(text$, SearchSource.USER_INPUT),
      this.prepareQuickSearchSource(this.searchInputFocus$, SearchSource.INPUT_FOCUS)
    ).pipe(
      tap(() => (this.searchingMemberList = true)),
      switchMap((term) => {
        const showingRecentlyViewed = isEmpty(term);
        const hasMemberSearchPermission = this.authService.isEntitled('/patients/getMemberListByCharacter.html');

        const recentlyViewedMembersObservable = this.headerService.getRecentlyViewedMembers();
        const searchMembersObservable = hasMemberSearchPermission
          ? this.headerService.getMemberList(term)
          : of([] as MemberSearch[]);

        const resultsObservable = showingRecentlyViewed ? recentlyViewedMembersObservable : searchMembersObservable;

        return resultsObservable.pipe(
          tap(() => {
            this.showingRecentlyViewed = showingRecentlyViewed;
          })
        );
      }),
      tap(() => (this.searchingMemberList = false)),
      catchError(() => {
        this.searchingMemberList = false;
        this.searchResults = [];
        return of([] as MemberSearch[]);
      }),
      finalize(() => (this.searchingMemberList = false))
    );

  onSelectMember(event: NgbTypeaheadSelectItemEvent<MemberSearch>) {
    this.navigateToPatientDetails(event.item.assetId);
  }

  fetchMemberId() {
    if (isString(this.searchValue)) {
      const searchValueUppercase = this.searchValue.toUpperCase();

      this.searching = true;
      this.patientService
        .getMemberIdByDisplayId(this.searchValue)
        .pipe(finalize(() => (this.searching = false)))
        .subscribe((memberId) => {
          if (memberId != null && memberId.length > 0) {
            this.navigateToPatientDetails(memberId);
          } else {
            this.globalModalService.alert({
              bodyText: `You do not have permission to access member ${searchValueUppercase}`
            });
          }
        });
    }
  }

  formatSearchInput(_value: MemberSearch): string {
    return null;
  }

  private navigateToPatientDetails(memberId: string) {
    this.router.navigate([`/private/patients/${memberId}/details`]).then((navigationSuccessful) => {
      if (navigationSuccessful) {
        this.searchValue = null;
      }
    });
  }

  sendNewMessage(sendMessage: TemplateRef<unknown>) {
    this.modalService.open(sendMessage);
  }

  switchOrganization(newOrg: Organization) {
    const currentOrg = this.authService.organization;
    this.searchValue = null;
    this.globalModalService
      .confirm({
        bodyText: "You are navigating away from your current organization. Are you sure you'd like to proceed?",
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel'
      })
      .then(
        () => {
          this.modalService.dismissAll();

          this.globalModalService
            .confirm({
              bodyText:
                `If you have other windows/tabs open for ${currentOrg.orgName}, ` +
                `please close them before navigating to ${newOrg.orgName}`
            })
            .then(
              () => {
                this.loadingIndicatorService.show();

                this.authService
                  .switchUserOrganization(newOrg.orgLevel)
                  .pipe(finalize(() => this.loadingIndicatorService.hide()))
                  .subscribe((data) => {
                    if (data) {
                      this.patientService.clearCache();

                      this.toastr.success(`Successfully switched organization to ${newOrg.orgName}`);
                      this.messageService.getUnreadMessageCount().subscribe();

                      // Redirecting to /login to get the authGuard to run again since LoginComponent
                      // does its own redirect to /private if you're already logged in. This solves
                      // issues when switching between accounts and one hasn't agreed to the T&C yet.
                      this.router.navigate(['/login']);
                    } else {
                      this.globalModalService.alert({
                        bodyText: `Failed to switch organization to ${newOrg.orgName}`
                      });
                    }
                  });
              },
              () => {
                // Intentionally empty
              }
            );
        },
        () => {
          // Intentionally empty
        }
      );
  }

  private getSupportInformation() {
    // This information is only relevant for unauthenticated users
    if (!this.authService.isAuthenticated()) {
      this.organizationService.getMappingKeys(SUPPORT_LINK_INFORMATION).subscribe((response) => {
        if (!isEmpty(response) && response[0].value) {
          this.supportInformation = response[0].value;
        } else {
          console.error(`${SUPPORT_LINK_INFORMATION} mapping key not configured`);
        }
      });
    }
  }
}
